<template>
  <div class="canonical-update-review">
    <p v-if="store.summaryText" class="canonical-update-review__summary">{{ store.summaryText }}</p>

    <ul v-if="topLevelErrors.length > 0" class="canonical-update-review__warning">
      <li v-for="(error, index) in topLevelErrors" :key="index">{{ error }}</li>
    </ul>

    <!-- Above the table: a confirm failure must be visible without scrolling past
         a long review. -->
    <div class="canonical-update-review__error" role="alert">
      {{ store.errorMessage }}
    </div>

    <p v-if="store.previewData" class="canonical-update-review__note">{{ store.labels.rejectedNote }}</p>

    <p v-if="showEmptyState" class="canonical-update-review__empty">{{ store.labels.emptyState }}</p>

    <!-- Rows exist but none are applyable (all unchanged/skipped/error), so the
         disabled Apply button needs an explanation. -->
    <p v-if="showNoActionableNote" class="canonical-update-review__empty">{{ store.labels.noActionableNote }}</p>

    <div v-if="rows.length > 0" class="preview-table">
      <table class="preview-table__table">
        <thead>
          <tr>
            <th class="preview-table__status">
              <!-- The column shows only status icons; the label is for assistive tech. -->
              <span class="preview-table__sr-only">{{ columnLabels.status }}</span>
            </th>
            <th>{{ columnLabels.name }}</th>
            <th>{{ columnLabels.type }}</th>
            <th>{{ columnLabels.changes }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="`${index}:${rowKey(row)}`"
            :data-testid="TestId.canonicalUpdate.previewRow"
          >
            <td class="preview-table__status">
              <RowStatusIcon :status="iconStatus(row)" :label="statusLabel(row)" />
            </td>
            <td>{{ row.name }}</td>
            <td>{{ row.type }}</td>
            <td :data-testid="TestId.canonicalUpdate.changesCell">
              <div class="preview-table__changes">
                <RowStatusIcon v-if="rowWarnings(row)" status="warning" :label="rowWarnings(row)" />
                <ChangeSetDiff :changeset="row.changes" :labels="diffLabels" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import ChangeSetDiff from "#ce/apps/SpAdmin/shared/components/ChangeSetDiff.ce.vue";
import RowStatusIcon from "#ce/apps/SpAdmin/shared/components/RowStatusIcon.ce.vue";
import { useCanonicalUpdateStore } from "#ce/apps/SpAdmin/SpCanonicalUpdateApp/store/canonical-update";

const store = useCanonicalUpdateStore();

// RowStatusIcon's "existing" variant is the update glyph, so "updated" rows map onto it.
// "unchanged" rows only reach the table when they carry warnings worth surfacing.
const ICON_STATUSES = {
  new: "new",
  updated: "existing",
  removed: "removed",
  skipped: "skipped",
  unchanged: "unchanged",
};

const rows = computed(() => store.rows);
const topLevelErrors = computed(() => store.previewData?.errors ?? []);
const columnLabels = computed(() => store.labels.columns ?? {});
// Top-level errors (malformed sheet, failed start) replace the empty state — zero rows
// alongside an error list must not read as "everything is in sync".
const showEmptyState = computed(
  () => Boolean(store.previewData) && rows.value.length === 0 && topLevelErrors.value.length === 0,
);
const showNoActionableNote = computed(() => rows.value.length > 0 && store.actionableRows.length === 0);
const diffLabels = computed(() => ({
  attributes: store.labels.attributes,
  removedValue: store.labels.removedValue,
}));

// Type-qualified name; the render :key prepends the loop index because both
// sheets can carry the same term.
function rowKey(row) {
  return `${row.type}:${row.name}`;
}

function iconStatus(row) {
  return ICON_STATUSES[row.status] ?? "error";
}

// Any row can carry `errors`. On error rows they are the failure, and on skipped
// rows the skip reason — either way the explanation of the status itself, so they
// belong in the status tooltip. On every other status they are nonfatal warnings
// (e.g. a specialty name that only resolved via mapping): the status icon shows
// the real outcome and the warnings surface as a triangle in the Changes cell.
function errorsExplainStatus(row) {
  return !(row.status in ICON_STATUSES) || row.status === "skipped";
}

function statusLabel(row) {
  const label = store.statusLabels[row.status] ?? "";

  if (!errorsExplainStatus(row)) {
    return label;
  }

  const reasons = row.errors?.join(", ") ?? "";

  if (label && reasons) {
    return `${label} — ${reasons}`;
  }

  return label || reasons;
}

function rowWarnings(row) {
  if (errorsExplainStatus(row)) {
    return "";
  }

  return row.errors?.join(", ") ?? "";
}
</script>

<style scoped lang="scss">
.canonical-update-review {
  display: flex;
  flex-direction: column;
  gap: var(--sp-ref-scale-16);

  &__summary,
  &__empty,
  &__note {
    font-size: var(--sp-sys-typescale-body-font-size);
    margin: 0;
  }

  &__note {
    font-size: var(--sp-sys-typescale-small-font-size);
    color: var(--sp-sys-color-on-surface-variant);
  }

  &__warning {
    // These are sheet-level errors: use the error token (the warning token's
    // yellow fails contrast on the light surface).
    color: var(--sp-sys-color-error);
    font-size: var(--sp-sys-typescale-small-font-size);
    margin: 0;
  }

  &__error {
    color: var(--sp-sys-color-error);
    font-size: var(--sp-sys-typescale-small-font-size);
    min-height: 1.25rem;
  }
}

.preview-table {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  border: var(--sp-ref-spacing-px) solid var(--sp-sys-color-outline-variant);
  border-radius: var(--sp-ref-scale-4);

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--sp-sys-typescale-small-font-size);

    th,
    td {
      padding: var(--sp-ref-scale-8);
      text-align: left;
      border-bottom: var(--sp-ref-spacing-px) solid var(--sp-sys-color-outline-variant);
    }

    th {
      font-size: var(--sp-ref-font-size-small);
      font-weight: var(--sp-ref-font-weight-normal);
      letter-spacing: calc(var(--sp-ref-spacing-px) * 0.5);
      color: var(--sp-sys-color-on-surface-variant);
      position: sticky;
      top: 0;
    }

    tbody tr {
      vertical-align: baseline;

      &:last-child td {
        border-bottom: none;
      }

      &:nth-child(even) {
        background-color: var(--sp-sys-color-surface-variant);
      }
    }
  }

  &__status {
    width: 2rem;
    text-align: center;
  }

  &__changes {
    display: flex;
    align-items: baseline;
    gap: var(--sp-ref-scale-4);
  }

  // Screen-reader-only text for the status column's assistive label.
  &__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
